import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeftIcon, FolderOpenIcon } from 'lucide-react';
import { type JSX, type ReactNode, useSyncExternalStore } from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import { SharePointTransferView } from '@/features/sharepoint/transfer/SharePointTransferView.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import {
  resolveCurrentUserRole,
  resolveDefaultWorkspaceId,
} from '@/features/workspace/workspace-access.js';
import { useConnectorPresence } from '@/integration/hooks/useConnections.js';
import {
  workspaceMembersQueryOptions,
  workspaceQueryOptions,
  workspacesQueryOptions,
} from '@/lib/api/workspaces.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

export const Route = createFileRoute('/artifacts_/transfer')({
  component: SharePointTransferPage,
});

/**
 * `/artifacts/transfer` — copying documents between the SharePoints the selected workspace reaches. It is
 * addressed under Artifacts because that is the section it belongs to and where it is entered from (the
 * SharePoint tab's own action), but it is NOT nested inside it: the screen is two full trees side by side
 * and wants the width, which the section's own column would take away.
 *
 * It gets no sidebar entry of its own. A top-level item is the most visible change this interface has, and
 * the job is one an editor reaches while already looking at SharePoint — the entry sits there instead, so
 * the navigation is unchanged and nothing new needs a runtime flag.
 *
 * Workspace resolution mirrors the Artifacts route: wait for the list, then take the persisted selection
 * (falling back to the user's default). The role is resolved WITHOUT platform-admin elevation, because the
 * copy endpoint has no admin bypass either — elevating would only render an action the service answers 403
 * to.
 */
function SharePointTransferPage(): JSX.Element {
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  const { data: workspaces, isError } = useQuery(workspacesQueryOptions());

  if (isError) {
    return (
      <PageShell>
        <p className="text-sm text-destructive">
          Couldn&apos;t load your workspaces. Check your connection and reload
          the page.
        </p>
      </PageShell>
    );
  }
  if (workspaces === undefined) {
    return (
      <PageShell>
        <Skeleton className="h-72 w-full rounded-xl" />
      </PageShell>
    );
  }

  const workspaceId = resolveDefaultWorkspaceId(
    workspaces,
    selectedWorkspaceId,
  );
  if (workspaceId === null) {
    return (
      <PageShell>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-16 text-center">
          <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/12 text-primary">
            <FolderOpenIcon className="size-6" />
          </span>
          <div className="max-w-md space-y-1">
            <p className="font-heading text-base font-semibold">
              Select a workspace
            </p>
            <p className="text-sm text-muted-foreground">
              Pick a workspace from the switcher to copy files between the
              SharePoints it reaches.
            </p>
          </div>
        </div>
      </PageShell>
    );
  }
  return <TransferRoute workspaceId={workspaceId} />;
}

/** Resolves the caller's role and the workspace's SharePoint connections, then renders the screen. */
function TransferRoute({ workspaceId }: { workspaceId: string }): JSX.Element {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const { data: workspace } = useQuery(workspaceQueryOptions(workspaceId));
  const { data: members } = useQuery(workspaceMembersQueryOptions(workspaceId));
  const role = resolveCurrentUserRole({
    workspace,
    members,
    currentUserId: identity.identity?.userId ?? null,
    isAdmin: false,
  });
  /**
   * Presence, and its CONNECTIONS rather than the flat fields: this screen is about which SharePoints exist
   * side by side, and the flat `connected` describes only whichever one the service resolved first.
   */
  const presence = useConnectorPresence(workspaceId);

  return (
    <PageShell>
      <SharePointTransferView
        key={workspaceId}
        workspaceId={workspaceId}
        role={role}
        connections={presence.data?.providers.sharepoint?.connections ?? []}
        isLoading={presence.isLoading}
        isError={presence.isError}
      />
    </PageShell>
  );
}

/** Title, the way back to the section this screen belongs to, and the page's width. */
function PageShell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex min-w-0 items-center gap-2">
        <Link
          to="/artifacts"
          search={{ tab: 'sharepoint' as const }}
          className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Back to SharePoint"
        >
          <ArrowLeftIcon className="size-4" />
        </Link>
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="font-heading text-xl font-bold tracking-tight text-foreground">
            Copy between SharePoints
          </h1>
          <p className="text-sm text-muted-foreground">
            Pick files in one SharePoint and a folder in another. The copies are
            made by the platform, so nothing is downloaded to this computer, and
            the originals stay where they are.
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}
